require('./brokerSubscriptionSuccess.scss');

const PropTypes = require('prop-types');

// @vendors
const React = require('react');
const moment = require('moment');

// @components
const { OKButton } = require('components/commons/OKButton/OKButton');
const { ContentBox } = require('commonsComponents/contentBox');
const { OKBoxTitle } = require('commonsComponents/OKBoxTitle');
const { OneLineSymbolAmount } = require('commonsComponents/oneLineSymbolAmount');

// @helpers
const { formatText, FormattedText, getLanguage } = require('core/i18n').i18n;
const { conditionalValueOrHyphen } = require('utilities/valueHelper');
const { buildFormattedDate } = require('utilities/dateHelper');
const { formatAmountObject, getPlainAmountObject } = require('utilities/currencyHelper');

// @constants
const { DATE_FORMAT_DAY_MONTH_YEAR, MYPROFILE_BROKER_URL } = require('constants/index');

const RETURN_TO_CORPORATES_OPERATIONS_LINK = '/myprofile/broker/corporatesOperations';

class BrokerSubscriptionSuccess extends React.Component {
    constructor(props) {
        super(props);

        this.buildHeader = this.buildHeader.bind(this);
        this.buildField = this.buildField.bind(this);
        this.buildLeftColumnDetails = this.buildLeftColumnDetails.bind(this);
        this.buildRightColumnDetails = this.buildRightColumnDetails.bind(this);
        this.buildFooter = this.buildFooter.bind(this);
    }

    buildField(primaryText, secondaryText, description = <span>&nbsp;</span>) {
        return (
            <div className="broker-subscription-success__details-field">
                <h5 className="broker-subscription-success__details-primary-text">
                    <FormattedText value={primaryText} format="uppercase" />
                </h5>
                <div className="broker-subscription-success__details-secondary-text">
                    {secondaryText}
                </div>
                <div className="broker-subscription-success__details-gray">
                    {description}
                </div>
            </div>
        );
    }

    buildFooter() {
        return (
            <div>
                <div className="wizard-success-step__separator" />
                <div className="wizard-success-step__foot-buttons text-right">
                    <span className="broker-subscription-success__corporates-operations-button">
                        <OKButton
                            modifier="ghost"
                            to={RETURN_TO_CORPORATES_OPERATIONS_LINK}
                        >
                            <FormattedText value="brokerSubscription-corporateOperations" />
                        </OKButton>
                    </span>
                    <OKButton
                        modifier="secondary"
                        to={MYPROFILE_BROKER_URL}
                    >
                        <FormattedText value="subscription-goToMyWallet" />
                    </OKButton>
                </div>
            </div>
        );
    }

    buildHeader() {
        const { immBrokerSubscription } = this.props;
        const orderNumber = immBrokerSubscription.get('orderNumber');

        return (
            <div>
                <i className="wizard-success-step__success-icon" />
                <div className="wizard-success-step__messages">
                    <OKBoxTitle value={formatText('subscription-successTitle')} />
                    <div className="wizard-success-step__success-sub-message">
                        <FormattedText
                            value="subscription-successSubtitle"
                            injectedStrings={[orderNumber]}
                        />
                    </div>
                </div>
            </div>
        );
    }

    buildLeftColumnDetails() {
        const { immBrokerSubscription, fullContractNumber } = this.props;
        const immAssociatedAccount = immBrokerSubscription.get('associatedAccount');
        const immValue = immBrokerSubscription.get('value');
        const amountObject = {
            amount: immAssociatedAccount.getIn(['balance', 'amount']),
            currency: immAssociatedAccount.getIn(['balance', 'currency'])
        };
        const plainAmountObject = getPlainAmountObject(amountObject);
        const contractOwnerText = (
            <FormattedText
                value="subscription-contractOwnerName"
                injectedStrings={[immBrokerSubscription.get('owner')]}
            />
        );
        const contractNumberText = (
            <FormattedText
                value="subscription-contractNumber"
                injectedStrings={[fullContractNumber]}
            />
        );

        const associatedAccountText = (
            <FormattedText
                value="subscription-associatedAccountId"
                injectedStrings={[immAssociatedAccount.get('number')]}
            />
        );
        const associatedAccountBalanceText = (
            <span>
                <FormattedText value="subscription-associatedAccountBalance" />
                {' '}
                <OneLineSymbolAmount
                    amountObject={plainAmountObject}
                    language={getLanguage()}
                    amountObjectFormatted={formatAmountObject(plainAmountObject, true)} />
            </span>
        );

        const stockMarketText = (
            <FormattedText
                value="brokerSubscription-stockMarket"
                injectedStrings={[immBrokerSubscription.get('stockMarket')]}
            />
        );

        const valueField = this.buildField(
            'subscription-value',
            immValue.get('descriptionValue'),
            stockMarketText
        );

        const contractField = this.buildField(
            'subscription-contract',
            immBrokerSubscription.get('walletAccountType')
        );

        return (
            <div className="broker-subscription-success__details-section-left">
                {valueField}
                {contractField}
                <div className="broker-subscription-success__details-gray">
                    <div className="broker-subscription-success__details-field">
                        {contractNumberText}
                        <br />
                        ({contractOwnerText})
                    </div>
                    <div>
                        {associatedAccountText}
                        <br />
                        ({associatedAccountBalanceText})
                    </div>
                </div>
            </div>
        );
    }

    buildRightColumnDetails() {
        const { immBrokerSubscription } = this.props;
        const startingDate = moment(immBrokerSubscription.get('startingDate'));
        const operationDate = moment(immBrokerSubscription.get('operationDate'));

        const startingDateFormatted = conditionalValueOrHyphen(
            startingDate.isValid(),
            buildFormattedDate(startingDate, DATE_FORMAT_DAY_MONTH_YEAR)
        );

        const operationDateFormatted = conditionalValueOrHyphen(
            operationDate.isValid(),
            buildFormattedDate(operationDate, DATE_FORMAT_DAY_MONTH_YEAR)
        );

        const startingDateField = this.buildField(
            'brokerSubscription-startingDate',
            startingDateFormatted
        );

        const operationDateField = this.buildField(
            'brokerSubscription-operationDate',
            operationDateFormatted
        );

        const sharesField = this.buildField(
            'brokerSubscription-sharesNumber',
            immBrokerSubscription.get('shares')
        );

        return (
            <div className="broker-subscription-success__details-section-right">
                {startingDateField}
                {operationDateField}
                {sharesField}
            </div>
        );
    }

    render() {
        return (
            <ContentBox>
                <h2 className="broker-subscription-success__success-operation-title">
                    <FormattedText value="subscription-successEndOperation" />
                </h2>
                {this.buildHeader()}
                <h3 className="broker-subscription-success__subscription-data-title">
                    <FormattedText value="brokerSubscription-subscriptionData" />
                </h3>
                <div className="row broker-subscription-success__details-container">
                    <div className="col-xs-4">
                        {this.buildLeftColumnDetails()}
                    </div>
                    <div className="col-xs-8">
                        {this.buildRightColumnDetails()}
                    </div>
                </div>
                {this.buildFooter()}
            </ContentBox>
        );
    }
}

BrokerSubscriptionSuccess.propTypes = {
    immBrokerSubscription: PropTypes.object.isRequired,
    fullContractNumber: PropTypes.string.isRequired
};

module.exports = BrokerSubscriptionSuccess;
